/**
 * @file     useSpeechToText.ts
 * @purpose  Thin wrapper over the browser SpeechRecognition (Web Speech API)
 *           for tap-to-dictate. Chrome ends a non-continuous session after one
 *           utterance (a couple of seconds) and still fires onend in continuous
 *           mode, so the hook keeps the session open until the user stops it.
 *           Reports `supported: false` where the API is absent (e.g. Firefox)
 *           so the caller can hide the mic or fall back to MediaRecorder.
 */
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

// Minimal local shapes — the Web Speech API isn't in lib.dom's stable types,
// and `webkitSpeechRecognition` is vendor-prefixed.
type SpeechResult = { isFinal: boolean; readonly 0: { transcript: string } };
type SpeechEvent = { resultIndex: number; results: { length: number } & Record<number, SpeechResult> };
type SpeechErrorEvent = { error?: string };
interface Recognition {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((e: SpeechEvent) => void) | null;
  onerror: ((e: SpeechErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type RecognitionCtor = new () => Recognition;

/** Chrome still closes a continuous session after a pause; restart just after onend. */
export const SPEECH_RESTART_AFTER_END_MS = 80;

const FATAL_SPEECH_ERRORS = new Set(['not-allowed', 'service-not-allowed']);

function getCtor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

function joinTranscript(committed: string, next: string): string {
  const piece = next.trim();
  if (!piece) return committed;
  return committed ? `${committed} ${piece}` : piece;
}

/** onTranscript(text, isFinal): text is the full session so far (interim
 *  updates arrive as the user speaks; isFinal marks a settled utterance). */
export function useSpeechToText(onTranscript: (text: string, isFinal: boolean) => void) {
  const [supported, setSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef<Recognition | null>(null);
  const cbRef = useRef(onTranscript);
  cbRef.current = onTranscript;
  const wantListeningRef = useRef(false);
  const committedRef = useRef('');
  const restartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startSessionRef = useRef<() => void>(() => {});

  useEffect(() => { setSupported(getCtor() != null); }, []);

  const clearRestartTimer = useCallback(() => {
    if (restartTimerRef.current == null) return;
    clearTimeout(restartTimerRef.current);
    restartTimerRef.current = null;
  }, []);

  const startSession = useCallback(() => {
    const Ctor = getCtor();
    if (!Ctor || !wantListeningRef.current) return;
    const rec = new Ctor();
    rec.lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
    rec.interimResults = true;
    rec.continuous = true;
    rec.onresult = (e) => {
      let newlyFinal = '';
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const piece = e.results[i][0].transcript;
        if (e.results[i].isFinal) newlyFinal += piece;
        else interim += piece;
      }
      if (newlyFinal) committedRef.current = joinTranscript(committedRef.current, newlyFinal);
      const text = joinTranscript(committedRef.current, interim);
      cbRef.current(text, newlyFinal.length > 0 && !interim);
    };
    rec.onerror = (e) => {
      if (!FATAL_SPEECH_ERRORS.has(e.error ?? '')) return;
      wantListeningRef.current = false;
      clearRestartTimer();
      setListening(false);
    };
    rec.onend = () => {
      if (!wantListeningRef.current) {
        setListening(false);
        return;
      }
      clearRestartTimer();
      restartTimerRef.current = setTimeout(() => {
        restartTimerRef.current = null;
        if (wantListeningRef.current) startSessionRef.current();
      }, SPEECH_RESTART_AFTER_END_MS);
    };
    recRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch {
      wantListeningRef.current = false;
      setListening(false);
    }
  }, [clearRestartTimer]);
  startSessionRef.current = startSession;

  const stop = useCallback(() => {
    wantListeningRef.current = false;
    clearRestartTimer();
    try { recRef.current?.stop(); } catch { /* already stopped */ }
    recRef.current = null;
    setListening(false);
  }, [clearRestartTimer]);

  const start = useCallback(() => {
    const Ctor = getCtor();
    if (!Ctor) return;
    wantListeningRef.current = false;
    clearRestartTimer();
    try { recRef.current?.abort(); } catch { /* noop */ }
    recRef.current = null;
    committedRef.current = '';
    wantListeningRef.current = true;
    startSession();
  }, [clearRestartTimer, startSession]);

  useEffect(() => () => {
    wantListeningRef.current = false;
    clearRestartTimer();
    try { recRef.current?.abort(); } catch { /* noop */ }
  }, [clearRestartTimer]);

  return { supported, listening, start, stop };
}
